import { axiosInstance } from '@/Api/axiosV2Config';
import type {
  Warranty,
  WarrantyFilters,
  CreateWarrantyDto,
  PropagateDiagnosticResult,
} from '../types/warranty.types';
import type {
  AssignWarrantyGcDto,
  WarrantyGcOptionsResponse,
  BulkAssignWarrantyGcEntry,
  BulkAssignWarrantyGcResult,
} from '@/features/societies/types/generalConditions.types';

/**
 * API client partagé pour les garanties
 * Lecture + mutations utilisées par la liste (admin et outil-pro)
 */
export const warrantyApi = {
  // ==================== Lecture ====================

  getFilterFacets: async (societyId?: string): Promise<{
    warrantyTypes: number[];
    vehicleTypes: number[];
    durations: number[];
    statuses: boolean[];
    /**
     * Valeurs de présence d'assistance réellement rencontrées, sur le modèle de
     * `statuses` : sans `true` dedans, la colonne n'afficherait qu'une colonne
     * de « Non » et le filtre ne trierait rien — on les masque tous les deux.
     */
    hasAssistance: boolean[];
  }> => {
    const { data } = await axiosInstance.get('/warranties-admin/filter-facets', {
      params: societyId ? { societyId } : undefined,
    });
    return data;
  },

  getWarranties: async (filters?: WarrantyFilters) => {
    const { data } = await axiosInstance.get('/warranties-admin', {
      params: filters,
    });
    return data;
  },

  getWarranty: async (uuid: string): Promise<Warranty> => {
    const { data } = await axiosInstance.get(`/warranties-admin/${uuid}`);
    return data;
  },

  getAvailableWarranties: async (societyId: string): Promise<Warranty[]> => {
    const { data } = await axiosInstance.get(`/warranties-admin/available/${societyId}`);
    return data;
  },

  // ==================== Mutations (liste) ====================

  activateWarranty: async (uuid: string): Promise<Warranty> => {
    const { data } = await axiosInstance.patch(`/warranties-admin/${uuid}/activate`);
    return data;
  },

  deactivateWarranty: async (uuid: string): Promise<Warranty> => {
    const { data } = await axiosInstance.patch(`/warranties-admin/${uuid}/deactivate`);
    return data;
  },

  updateWarranty: async (uuid: string, warranty: Record<string, any>): Promise<Warranty> => {
    const { data } = await axiosInstance.put(`/warranties-admin/${uuid}`, warranty);
    return data;
  },

  /**
   * Propage les "Informations complémentaires" (champ `diagnostic`) de la
   * garantie source vers toutes les autres garanties de la même société.
   * Réservé aux utilisateurs internes (vérifié côté API).
   */
  propagateDiagnostic: async (uuid: string): Promise<PropagateDiagnosticResult> => {
    const { data } = await axiosInstance.post<PropagateDiagnosticResult>(
      `/warranties-admin/${uuid}/propagate-diagnostic`,
    );
    return data;
  },

  /**
   * Déplace une garantie avant/après une de ses voisines.
   *
   * On envoie un repère relatif plutôt qu'une valeur d'ordre : la liste
   * affichée est filtrée et paginée, donc un rang visible ne correspond pas au
   * rang réel en base. L'API renumérote la société de 1 à n à chaque appel.
   */
  moveWarranty: async (
    uuid: string,
    targetUuid: string,
    position: 'before' | 'after',
  ): Promise<Warranty> => {
    const { data } = await axiosInstance.patch(`/warranties-admin/${uuid}/move`, {
      targetUuid,
      position,
    });
    return data;
  },

  duplicateWarranty: async (uuid: string, newName?: string): Promise<Warranty> => {
    const { data } = await axiosInstance.post(`/warranties-admin/${uuid}/duplicate`, { newName });
    return data;
  },

  // ==================== Provisions ====================

  getProvisionsPreview: async (societyId: string): Promise<{ contractsCount: number }> => {
    const { data } = await axiosInstance.get(`/warranties-admin/societies/${societyId}/provisions-preview`);
    return data;
  },

  updateProvisionsForSociety: async (societyId: string): Promise<{ total: number }> => {
    const { data } = await axiosInstance.post(`/warranties-admin/societies/${societyId}/update-provisions`);
    return data;
  },

  // ==================== Copie inter-société ====================

  copyToSociety: async (sourceSocietyId: string, targetSocietyId: string): Promise<{ copied: number }> => {
    const { data } = await axiosInstance.post('/warranties-admin/copy-to-society', { sourceSocietyId, targetSocietyId });
    return data;
  },

  // ==================== Création ====================

  createWarranty: async (dto: CreateWarrantyDto): Promise<Warranty> => {
    const { data } = await axiosInstance.post<Warranty>('/warranties-admin', dto);
    return data;
  },

  // ==================== Conditions Générales ====================

  /**
   * Liste les versions CG éligibles pour une garantie + une variante (cash/stripe)
   */
  getGcOptions: async (
    uuid: string,
    variant: 'cash' | 'stripe',
  ): Promise<WarrantyGcOptionsResponse> => {
    const { data } = await axiosInstance.get<WarrantyGcOptionsResponse>(
      `/warranties-admin/${uuid}/general-conditions/options`,
      { params: { variant } },
    );
    return data;
  },

  /**
   * Met à jour les FKs CG (cash et/ou stripe) d'une garantie individuelle.
   * Chaque champ est optionnel : null = défaut Affinicar.
   */
  assignGeneralConditions: async (
    uuid: string,
    dto: AssignWarrantyGcDto,
  ): Promise<{
    uuid: string;
    v2GeneralConditionsIdCash: string | null;
    v2GeneralConditionsIdStripe: string | null;
  }> => {
    const { data } = await axiosInstance.patch(
      `/warranties-admin/${uuid}/general-conditions`,
      dto,
    );
    return data;
  },

  /**
   * Télécharge le PDF des conditions particulières d'une garantie pour une variante donnée.
   */
  downloadConditionsPdf: async (uuid: string, variant: 'cash' | 'stripe' = 'cash'): Promise<Blob> => {
    const { data } = await axiosInstance.get(
      `/warranties-admin/${uuid}/pdf`,
      { params: { variant }, responseType: 'blob' },
    );
    return data;
  },

  /**
   * Assigne en masse les FKs CG (cash et/ou stripe) à plusieurs garanties.
   * Utilisé par le modal d'assignation global de l'onglet Conditions.
   */
  bulkAssignGeneralConditions: async (
    assignments: BulkAssignWarrantyGcEntry[],
  ): Promise<BulkAssignWarrantyGcResult> => {
    const { data } = await axiosInstance.post<BulkAssignWarrantyGcResult>(
      `/warranties-admin/general-conditions/bulk-assign`,
      { assignments },
    );
    return data;
  },
};
